import React from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import ColorInput from './ColorInput';
import { FONT_FAMILY_OPTIONS, COLOR_FIELDS } from '../../../config/constants';

/**
 * ThemeForm Component
 *
 * The main form for creating and editing themes with sections for:
 * - Basic information
 * - Color palette
 * - Typography settings
 * - Layout & spacing
 * - Assets
 * - Custom CSS
 */
const ThemeForm = ({
  formData,
  currentThemeItem,
  onChange,
  colorFields = COLOR_FIELDS,
}) => {
  const getFontFamilyOptions = () => FONT_FAMILY_OPTIONS;

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {/* Basic Information */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          autoFocus
          name="theme_name"
          label="Theme Name"
          fullWidth
          variant="outlined"
          value={formData.theme_name}
          onChange={onChange}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="theme_code"
          label="Theme Code"
          fullWidth
          variant="outlined"
          value={formData.theme_code}
          onChange={onChange}
          disabled={!!currentThemeItem}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          name="description"
          label="Description"
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          value={formData.description}
          onChange={onChange}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Color Palette */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Color Palette
        </Typography>
      </Grid>
      {colorFields.map((colorField, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={colorField.name}>
          <ColorInput
            name={colorField.name}
            label={colorField.label}
            value={formData[colorField.name]}
            onChange={onChange}
          />
        </Grid>
      ))}

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Typography */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Typography
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth>
          <InputLabel>Font Family</InputLabel>
          <Select
            name="font_family"
            value={formData.font_family}
            label="Font Family"
            onChange={onChange}
          >
            {getFontFamilyOptions().map(font => (
              <MenuItem key={font} value={font}>
                {font}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="font_size_base"
          label="Base Font Size (px)"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.font_size_base}
          onChange={onChange}
          inputProps={{ min: 12, max: 24 }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Layout & Spacing */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Layout & Spacing
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="border_radius"
          label="Border Radius (px)"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.border_radius}
          onChange={onChange}
          inputProps={{ min: 0, max: 20 }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="spacing_unit"
          label="Spacing Unit (px)"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.spacing_unit}
          onChange={onChange}
          inputProps={{ min: 4, max: 16 }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Assets */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Assets
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="logo_url"
          label="Logo URL"
          fullWidth
          variant="outlined"
          value={formData.logo_url}
          onChange={onChange}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="favicon_url"
          label="Favicon URL"
          fullWidth
          variant="outlined"
          value={formData.favicon_url}
          onChange={onChange}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Custom CSS */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Custom CSS
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          name="custom_css"
          label="Custom CSS"
          fullWidth
          multiline
          rows={6}
          variant="outlined"
          value={formData.custom_css}
          onChange={onChange}
          placeholder="/* Add your custom CSS here */"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={onChange}
              name="is_active"
            />
          }
          label="Active Theme"
        />
      </Grid>
    </Grid>
  );
};

export default ThemeForm;
